import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type SizeKey = 'sm' | 'md' | 'lg';
type Variant = 'glass' | 'solid' | 'ghost';

const SIZE_CLASSES: Record<SizeKey, string> = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-base',
};

const GLASS_CLASSES = [
  'bg-white/[0.06] hover:bg-white/[0.12]',
  'backdrop-blur-xl backdrop-saturate-[1.8]',
  'border border-white/[0.12] hover:border-white/[0.22]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_4px_24px_-8px_rgba(0,0,0,0.4)]',
  'active:scale-[0.98] transition-all duration-200',
].join(' ');

const SOLID_CLASSES = [
  'bg-gradient-to-br from-honey-400 to-honey-600 hover:from-honey-300 hover:to-honey-500',
  'text-ink-950 font-bold',
  'shadow-[0_8px_28px_-8px_rgba(245,158,11,0.7),inset_0_1px_0_rgba(255,255,255,0.4)]',
  'active:scale-[0.98] transition-all duration-200',
].join(' ');

const GHOST_CLASSES = [
  'text-cream-200 hover:text-white hover:bg-white/[0.06]',
  'transition-all duration-200',
].join(' ');

const VARIANT: Record<Variant, string> = {
  glass: GLASS_CLASSES,
  solid: SOLID_CLASSES,
  ghost: GHOST_CLASSES,
};

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold text-white whitespace-nowrap';

interface CommonProps {
  children: ReactNode;
  size?: SizeKey;
  variant?: Variant;
  className?: string;
}

type LinkVariantProps = CommonProps & { to: string; href?: never; onClick?: never };
type AnchorVariantProps = CommonProps & {
  href: string;
  to?: never;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
};
type ButtonVariantProps = CommonProps & {
  to?: never;
  href?: never;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  type?: 'button' | 'submit';
  ariaLabel?: string;
  disabled?: boolean;
};

type Props = LinkVariantProps | AnchorVariantProps | ButtonVariantProps;

export function GlassButton(props: Props): JSX.Element {
  const { children, size = 'md', variant = 'glass', className = '' } = props;
  const classes = `${BASE_CLASSES} ${SIZE_CLASSES[size]} ${VARIANT[variant]} ${className}`;

  if ('to' in props && props.to !== undefined) {
    return <Link to={props.to} className={classes}>{children}</Link>;
  }
  if ('href' in props && props.href !== undefined) {
    return (
      <a
        href={props.href}
        className={classes}
        {...(props.target !== undefined ? { target: props.target } : {})}
        {...(props.rel !== undefined ? { rel: props.rel } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      disabled={props.disabled ?? false}
      aria-label={props.ariaLabel}
      className={`${classes} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}
